import { useDynamicsRouteMeta } from "@/hooks/useDynamicsRouteMeta";
import { useLeadFilterParams } from "./useLeadFilterParams";
import { useDynamicFilterParams } from "@/features/dynamic/api/useDynamicFilterParams";

/**
 * The serialized filter params for WHICHEVER lead surface is currently open.
 *
 * WHY THIS EXISTS. Lead filters live in TWO different places:
 *   - the `/leads` page              → `state.leadFilterSlice`  (`useLeadFilterParams`)
 *   - stage tabs and Favorite Views  → `state.dynamicFilter[routeId]`
 *                                      (`useDynamicFilterParams(routeId)`)
 *
 * Anything shared by both surfaces — the lead drawer, most obviously — that
 * reads only one of them is silently inert on the other. That is not
 * hypothetical: the drawer's "which deal did the filter match" logic read the
 * `/leads` slice, so on a Favorite View (`/views/offer-issued-nov-march-…`,
 * the surface the bug was reported on) its intake list came back empty, every
 * deal "matched", and it fell back to picking the lead's furthest deal — the
 * one carrying an intake the user had explicitly not selected.
 *
 * Read the ACTIVE surface's params through this hook instead of picking a
 * slice. `routeId` is `"default"` off a dynamic route, and
 * `selectDynamicFilters` returns its default state for an unknown id, so both
 * underlying hooks are safe to call unconditionally (Rules of Hooks).
 */
export function useActiveLeadFilterParams(): Record<string, any> {
  const { routeId, isDynamicsRoute, isFavoriteViewRoute } =
    useDynamicsRouteMeta();

  const leadsParams = useLeadFilterParams();
  const dynamicParams = useDynamicFilterParams(routeId ?? "default");

  const onDynamicRoute =
    (isDynamicsRoute || isFavoriteViewRoute) &&
    routeId != null &&
    String(routeId) !== "" &&
    String(routeId) !== "default";

  return onDynamicRoute ? dynamicParams : leadsParams;
}

/**
 * The intake (and any other correlated custom-field) combinations currently
 * filtered on, as `[optionA, optionB]` pairs, parsed straight off the wire
 * params (`cfPair_<fieldA>_<fieldB>=<a>-<b>,…`).
 *
 * Parsing the SERIALIZED form rather than a slice is deliberate: it is
 * identical on every surface and it is exactly what the server receives, so a
 * consumer can never disagree with the query that produced the list.
 */
export function parseCfPairsFromParams(
  params: Record<string, any> | undefined,
): Array<[string, string]> {
  const out: Array<[string, string]> = [];
  for (const [key, value] of Object.entries(params ?? {})) {
    if (!key.startsWith("cfPair_") || !value) continue;
    for (const token of String(value).split(",")) {
      const [a, b] = token.trim().split("-");
      if (a && b) out.push([a, b]);
    }
  }
  return out;
}
